import type { LangCode } from '@/types/config'

/**
 * BCP 47 语言代码到后端 API 语言字段的映射
 *
 * 后端接口返回的多语言字段使用旧格式：
 * - langValueZh (中文)
 * - langValueEn (英文)
 * - langValueVi (越南语)
 *
 * 前端使用 BCP 47 标准，需要映射
 */

// 映射类型
type BackendLangField =
  | 'langValueZh'
  | 'langValueEn'
  | 'langValueVi'
  | 'langValueHi'
  | 'langValueTa'
  | 'langValueMs'
  | 'langValuePt'

type NameLangField = 'nameZh' | 'nameEn' | 'nameVi'
type LangField = BackendLangField | NameLangField
type LangObj = Partial<Record<LangField, string>>

/**
 * 所有可能的多语言字段映射表
 * - 数组顺序决定优先级
 * - 可以根据项目后端实际字段增加
 */
const localeFieldMap: Record<LangCode, (BackendLangField | NameLangField)[]> = {
  'zh-CN': ['langValueZh', 'nameZh'],
  'en-US': ['langValueEn', 'nameEn'],
  'vi-VN': ['langValueVi', 'nameEn'],
  'hi-IN': ['langValueHi', 'nameEn'], // 印地语名称
  'ta-IN': ['langValueTa', 'nameEn'],
  'ms-MY': ['langValueMs', 'nameEn'], // 马来语名称
  'pt-BR': ['langValuePt', 'nameEn'] // 巴西语名称
}

export type LegacyLocale = LangCode | 'zh' | 'en' | 'vi'

/**
 * 前端旧值兼容
 */
const legacyToModern: Record<LegacyLocale, LangCode> = {
  'zh-CN': 'zh-CN',
  'en-US': 'en-US',
  'vi-VN': 'vi-VN',
  'hi-IN': 'hi-IN',
  'ta-IN': 'ta-IN',
  'ms-MY': 'ms-MY',
  'pt-BR': 'pt-BR',
  zh: 'zh-CN',
  en: 'en-US',
  vi: 'vi-VN'
}

export const localeToLegacyCode = (locale: LegacyLocale): LangCode =>
  legacyToModern[locale] || 'en-US'

export const localPartialKeyMap: Record<LangCode, string> = {
  'zh-CN': 'Zh',
  'en-US': 'En',
  'vi-VN': 'Vi',
  'hi-IN': 'Hi',
  'ta-IN': 'Ta',
  'ms-MY': 'Ms',
  'pt-BR': 'Pt'
}

/**
 * 从多语言对象中获取当前语言的值
 *
 * 优先级：
 * 1. localeFieldMap 中字段顺序
 * 2. fallback
 *
 * @param langObj 包含多语言字段的对象
 * @param locale 当前语言代码（BCP 47，如 'zh-CN', 'en-US', 'vi-VN'）
 * @param fallback 默认值，当没有匹配字段时返回
 * @returns 当前语言对应的值，若无则返回 fallback
 *
 * @example
 * const item = {
 *   langValueZh: '中文',
 *   langValueEn: 'English',
 *   langValueVi: 'Tiếng Việt'
 * }
 * getLangValue(item, 'zh-CN') // '中文'
 * getLangValue(item, 'en-US') // 'English'
 */
export const getLangValue = (langObj: LangObj, locale: LangCode, fallback: string = ''): string => {
  const fields = localeFieldMap[locale] || []

  for (const key of fields) {
    const val = (langObj as Record<string, unknown>)[key]
    if (typeof val === 'string' && val.trim() !== '') {
      return val
    }
  }

  return fallback
}

/**
 * 从多语言对象中提取对应语言的值，并返回新对象
 *
 * @param langObj 包含多语言字段的对象
 * @param locale 当前语言代码
 * @param targetKey 目标键名
 * @returns 包含目标键的新对象
 *
 * @example
 * const item = {
 *   id: 1,
 *   langValueZh: '中文',
 *   langValueEn: 'English'
 * }
 * extractLangValue(item, 'zh-CN', 'name')
 * // { id: 1, name: '中文', langValueZh: '中文', langValueEn: 'English' }
 */
export const extractLangValue = <T extends Record<string, unknown>>(
  langObj: T,
  locale: LangCode,
  targetKey: string = 'langValue'
): T & Record<string, string> => {
  const value = getLangValue(langObj, locale, '')
  return {
    ...langObj,
    [targetKey]: value
  }
}

/**
 * 更通用的多语言字段 key 生成器
 * 根据前缀、locale 和后缀生成完整的字段名
 *
 * @param prefix 字段前缀（如 'iconName', 'image', 'imagePc'）
 * @param locale 当前语言代码
 * @param suffix 字段后缀（如 '6', 'Url', 'UrlPc'）
 * @returns 完整的字段名
 *
 * @example
 * getLocaleFieldKey('iconName', 'zh-CN', '6') // 'iconNameZh6'
 * getLocaleFieldKey('image', 'en-US', 'Url') // 'imageEnUrl'
 * getLocaleFieldKey('image', 'vi-VN', 'UrlPc') // 'imageViUrlPc'
 */
export function getLocaleFieldKey(prefix: string, locale: LangCode, suffix: string = ''): string {
  return `${prefix}${localPartialKeyMap[locale]}${suffix}`
}

/**
 * 类型安全的通用多语言字段访问器
 * 支持自动 fallback 到常用语言（En, Zh, Vi）
 *
 * @template T - 对象类型
 * @param obj - 包含多语言字段的对象
 * @param locale - 当前语言
 * @param prefix - 字段前缀（如 'iconName', 'image', 'imagePc'）
 * @param suffix - 字段后缀（如 '6', 'Url', 'UrlPc'）
 * @param fallback - 默认值
 * @returns 当前语言对应的值，若无则按优先级返回 fallback
 *
 * @example
 * const item = { iconNameEn6: 'English', iconNameZh6: '中文' }
 * getLocaleField(item, 'zh-CN', 'iconName', '6') // '中文'
 * getLocaleField(item, 'en-US', 'iconName', '6') // 'English'
 * getLocaleField(item, 'vi-VN', 'iconName', '6')
 */
export function getLocaleField<T extends Record<string, any>>(
  obj: T,
  locale: LangCode,
  prefix: string,
  suffix: string = '',
  fallback: string = ''
): string {
  // 尝试获取当前语言的字段
  const key = getLocaleFieldKey(prefix, locale, suffix)
  const value = obj[key]
  if (value !== null && value !== '') {
    return value
  }

  // Fallback 顺序：En -> Zh -> Vi
  const fallbackLocales: LangCode[] = ['en-US', 'zh-CN', 'vi-VN']
  for (const fallbackLocale of fallbackLocales) {
    if (fallbackLocale === locale) continue
    const fallbackKey = getLocaleFieldKey(prefix, fallbackLocale, suffix)
    const fallbackValue = obj[fallbackKey]
    if (fallbackValue !== null && fallbackValue !== '') {
      return fallbackValue
    }
  }

  return fallback
}
